import { Link, useLocation } from "@tanstack/react-router";
import type { FC, ReactNode } from "react";
import { GitPullLogoIcon, TableOfContentsIcon } from "@/components/Icons";
import { DebugSelect } from "@/components/ui/DebugSelect";
import { DebugToggleGroup } from "@/components/ui/DebugToggleGroup";
import { DomainBadge } from "@/components/ui/DomainBadge";
import { SchemeSelect } from "@/components/ui/SchemeSelect";
import { SchemeToggleGroup } from "@/components/ui/SchemeToggleGroup";
import { ThemeSelect } from "@/components/ui/ThemeSelect";
import { ThemeToggleGroup } from "@/components/ui/ThemeToggleGroup";
import { VariantSelect } from "@/components/ui/VariantSelect";
import { VariantToggleGroup } from "@/components/ui/VariantToggleGroup";
import { getContent, topNavContent } from "@/content";
import { useTopNavOptional } from "@/context/TopNavContext";
import { useVariant } from "@/context/VariantContext";
import { transitions } from "@/styles/transitions";
import type { ComponentSize } from "@/types/component-props";
import { cn } from "@/utils/cn";

export interface TopNavProps {
  /**
   * Additional content to display in the navigation
   */
  children?: ReactNode;

  /**
   * Optional class name for additional styling
   */
  className?: string;

  /**
   * Size for color scheme selector: 'sm' for small size
   */
  selectorSize?: ComponentSize;

  /**
   * Handler for mobile menu toggle (clicking logo on mobile)
   */
  onMobileMenuToggle?: () => void;
}

/**
 * TopNav component for the website
 * Provides consistent header with theme controls
 */
export const TopNav: FC<TopNavProps> = (props = {}) => {
  const context = useTopNavOptional();
  const { variant } = useVariant();
  const { pathname } = useLocation();
  const navContent = getContent(topNavContent, variant);

  // Determine if debug toggle should be visible:
  // - Always show on /demo/* routes
  // - Show on other routes only if ?debug=1 is in URL
  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : "",
  );
  const hasDebugParam = searchParams.get("debug") === "1";
  const isDemoRoute = pathname.startsWith("/demo");
  const showDebugToggle = isDemoRoute || hasDebugParam;

  // Use props if provided, otherwise fallback to context
  const children = props.children ?? context?.navigationContent;
  const className = props.className ?? "";
  const selectorSize = props.selectorSize ?? context?.selectorSize ?? "default";
  const onMobileMenuToggle =
    props.onMobileMenuToggle ?? context?.onMobileMenuToggle;
  const showMobileMenuButton = !!onMobileMenuToggle;

  const _isSmallSelector = selectorSize === "sm";

  return (
    <div
      className={`sticky top-0 z-[var(--topnav-z-index,40)] ${className}`}
      data-testid="top-nav"
    >
      {/* Upper menu */}
      <nav
        className="flex h-[var(--topnav-height,3rem)] w-full place-items-center border-b border-gray-200 dark:border-gray-700/30 bg-white dark:bg-gray-900 text-sm backdrop-blur-sm bg-white/90 dark:bg-gray-900/95"
        id="top-nav"
      >
        <div className="max-w-[900px] mx-auto h-12 w-full px-2 md:px-0">
          <div className="flex justify-between items-center h-12 w-full">
            {/* Logo - always links to home */}
            <Link
              activeProps={{ className: "flex items-center group" }}
              className="flex items-center group"
              to="/"
            >
              <GitPullLogoIcon
                svgClassName={`h-8 w-8 text-black dark:text-white ${transitions.colors}`}
                wrapperClassName="inline-flex"
              />
              <div className="ml-2 flex items-center relative group">
                {/* Main name */}
                <div className="text-sm md:text-xl font-semibold tracking-wide transition-all duration-300">
                  <span className="bg-gradient-to-r from-gray-800 to-gray-900 dark:from-gray-100 dark:to-white bg-clip-text text-transparent">
                    {navContent.firstName}
                  </span>
                  <span className="text-theme-primary ml-1">
                    {navContent.lastName}
                  </span>
                </div>

                {/* Domain badge - hidden on mobile */}
                <div className="ml-2">
                  <DomainBadge
                    className="group-hover:bg-theme-secondary/20"
                    domain={navContent.domain}
                    showOnMobile={false}
                    variant="secondary"
                  />
                </div>

                {/* Subtle glow effect on hover */}
                <div className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-30 group-hover:bg-theme-primary/10 blur-md transition-all duration-300" />
              </div>
            </Link>

            <div className="flex items-center gap-1.5">
              {/* Debug toggle - conditionally visible */}
              {showDebugToggle && (
                <>
                  <div className="hidden md:block">
                    <DebugToggleGroup />
                  </div>
                  <div className="block md:hidden">
                    <DebugSelect />
                  </div>
                </>
              )}

              {/* Variant switcher (UK/US) - desktop version */}
              <div className="hidden md:block">
                <VariantToggleGroup />
              </div>

              {/* Variant switcher - mobile version */}
              <div className="block md:hidden">
                <VariantSelect />
              </div>

              {/* Theme selector (color palettes) - mobile only */}
              <div className="block md:hidden">
                <ThemeSelect />
              </div>

              {/* Theme toggle group (color palettes) - tablet/desktop only */}
              <div className="hidden md:block">
                <ThemeToggleGroup size={selectorSize} />
              </div>

              {/* Color scheme toggle (light/dark/system) - desktop version */}
              <div className="hidden md:block">
                <SchemeToggleGroup />
              </div>

              {/* Color scheme dropdown - mobile version */}
              <div className="block md:hidden">
                <SchemeSelect />
              </div>

              {/* Mobile menu toggle button - only shown on mobile when handler provided */}
              {showMobileMenuButton && (
                <div className="block md:hidden">
                  <button
                    aria-label="Toggle navigation menu"
                    className={cn(
                      "flex items-center justify-center h-10 w-10 text-sm cursor-pointer",
                      transitions.colors,
                      "bg-white/95 dark:bg-gray-800/95 border-gray-100 dark:border-gray-700",
                      "border rounded-md shadow-[0_1px_2px_0_rgba(0,0,0,0.03)] dark:shadow-sm",
                      "hover:bg-gray-100 dark:hover:bg-gray-700/50",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary/40 dark:focus-visible:ring-theme-primary/40",
                    )}
                    onClick={onMobileMenuToggle}
                    type="button"
                  >
                    <TableOfContentsIcon
                      svgClassName="h-5 w-5 text-gray-700 dark:text-gray-300"
                      wrapperClassName="inline-flex"
                    />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Navigation section */}
      {children && (
        <div className="container mx-auto px-4 pb-3">
          <div className="w-full">{children}</div>
        </div>
      )}
    </div>
  );
};
